import React, { useState, useEffect, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import MUIDataTable from 'mui-datatables';
import { Icon } from '@iconify/react';
import { useNavigate } from 'react-router-dom';
import Swal from 'sweetalert2';
import { getOrders, deleteOrder, updateOrderStatus } from '../Redux/Reducers/ProductSlice';
import DefaultAvatar from '../otherImages/default.png';
import { getPaginationTotal, getRowsPerPageOptions } from '../helper/tablePagination';
import useTableUrlState from '../hook/useTableUrlState';
import { getSortOrder, sortTableData } from '../helper/tableSort';

const OrdersDataTable = ({ statusFilter, setStatusFilter }) => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const [openDropdown, setOpenDropdown] = useState(null);
  const [dropdownDirection, setDropdownDirection] = useState('down');
  const [
    { filter, page, rowsPerPage, searchText, sortName, sortDirection },
    setTableState,
    getReturnState,
  ] = useTableUrlState({
    filter: 'all',
    page: 0,
    rowsPerPage: 12,
    searchText: '',
    sortName: '',
    sortDirection: '',
  });
  const [isSearchUpdate, setIsSearchUpdate] = useState(false);
  
  const { orders, loading, error, deletingOrder, updatingOrderStatus, ordersPagination } = useSelector((state) => state.products || { orders: [], loading: false, error: null, deletingOrder: false, updatingOrderStatus: false, ordersPagination: null });
  const ordersTotal = getPaginationTotal(ordersPagination, orders.length);
  const hasSearch = Boolean(searchText?.trim());
  const isLocalFilteredView = !hasSearch && (filter !== 'all' || statusFilter !== 'all');
  const requestPage = isLocalFilteredView ? 1 : page + 1;
  const requestPerPage = isLocalFilteredView
    ? Math.max(ordersTotal, rowsPerPage)
    : rowsPerPage;

  const statusOptions = [
    { label: 'pending', value: 'pending' },
    { label: 'processing', value: 'processing' },
    { label: 'shipped', value: 'delivered' },
  ];
  
  // Status filter options for dropdown
  const statusFilterOptions = [
    { value: 'all', label: 'All Orders' },
    { value: 'pending', label: 'Pending' },
    { value: 'processing', label: 'Processing' },
    { value: 'delivered', label: 'Shipped' },
  ];

  const getStatusColor = (status) => {
    switch(status?.toLowerCase()) {
      case 'pending': return 'bg-warning-100 text-warning-800';
      case 'processing': return 'bg-primary-100 text-primary-800';
      case 'delivered': return 'bg-success-100 text-success-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  useEffect(() => {
    dispatch(
      getOrders({
        page: requestPage,
        perPage: requestPerPage,
        search: hasSearch ? searchText : '',
      }),
    );
  }, [dispatch, hasSearch, requestPage, requestPerPage, searchText]);

  useEffect(() => {
    if (!loading) {
      setIsSearchUpdate(false);
    }
  }, [loading]);

  const handleDeleteOrder = async (orderId) => {
    const result = await Swal.fire({
      title: 'Are you sure?',
      text: "You won't be able to revert this!",
      icon: 'warning',
      confirmButtonColor: '#000',
      confirmButtonText: 'Yes, delete it!',
    });

    if (result.isConfirmed) {
      try {
        const response = await dispatch(deleteOrder(orderId)).unwrap();
        if (response?.success) {
          Swal.fire({ icon: 'success', title: 'Deleted!', text: response?.message || 'Order has been deleted successfully.', timer: 2000, showConfirmButton: false });
        } else {
          Swal.fire({ icon: 'error', title: 'Error!', text: response?.message || 'Failed to delete order.' });
        }
      } catch (error) {
        Swal.fire({ icon: 'error', title: 'Error!', text: error || 'Something went wrong while deleting the order.' });
      }
    }
  };

  const handleStatusChange = async (orderId, newStatus) => {
    setOpenDropdown(null);
    try {
      const response = await dispatch(updateOrderStatus({ orderId, status: newStatus })).unwrap();
      if (response?.success) {
        Swal.fire({ icon: 'success', title: 'Status Updated!', text: `Order status changed to ${newStatus}`, timer: 1500, showConfirmButton: false });
      } else {
        Swal.fire({ icon: 'error', title: 'Error!', text: response?.message || 'Failed to update status.' });
      }
    } catch (error) {
      Swal.fire({ icon: 'error', title: 'Error!', text: error || 'Something went wrong while updating status.' });
    }
  };

  const handleStatusDropdownToggle = (event, orderId, isOpen) => {
    if (isOpen) {
      setOpenDropdown(null);
      return;
    }

    const rect = event.currentTarget.getBoundingClientRect();
    const dropdownHeight = statusOptions.length * 37 + 8;
    const spaceBelow = window.innerHeight - rect.bottom;
    const spaceAbove = rect.top;

    setDropdownDirection(spaceBelow < dropdownHeight && spaceAbove > spaceBelow ? 'up' : 'down');
    setOpenDropdown(orderId);
  };

  const getPaymentMethodIcon = (method) => {
    switch(method?.toLowerCase()) {
      case 'credit card': return 'mdi:credit-card';
      case 'bank transfer': return 'mdi:bank-transfer';
      case 'paypal': return 'mdi:paypal';
      default: return 'mdi:payment';
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return '-';
    const date = new Date(dateString);
    return date.toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' });
  };

  const getUniqueProducts = (order) => {
    if (!order.items || order.items.length === 0) return '-';
    const uniqueProducts = new Map();
    order.items.forEach(item => {
      const key = item.group_uuid || item.product_name;
      if (!uniqueProducts.has(key)) uniqueProducts.set(key, item.product_name);
    });
    return Array.from(uniqueProducts.values()).join(', ');
  };

  const filteredData = useMemo(() => {
    let result = orders;
    
    // Apply date filter
    if (filter === 'monthly' && !hasSearch) {
      const currentMonth = new Date().getMonth();
      const currentYear = new Date().getFullYear();
      result = result.filter(order => {
        const orderDate = new Date(order.created_at);
        return orderDate.getMonth() === currentMonth && orderDate.getFullYear() === currentYear;
      });
    }
    
    // Apply status filter
    if (statusFilter !== 'all') {
      result = result.filter(order => order.status?.toLowerCase() === statusFilter.toLowerCase());
    }
    
    return result;
  }, [orders, filter, hasSearch, statusFilter]);

  const sortedData = useMemo(
    () =>
      sortTableData(filteredData, sortName, sortDirection, {
        user: (order) =>
          order?.user?.name ||
          `${order?.user?.first_name || ''} ${order?.user?.last_name || ''}`,
        products: getUniqueProducts,
        total_amount: (order) =>
          order?.total_amount ||
          order?.summary?.total_amount ||
          order?.summary?.subtotal ||
          0,
      }),
    [filteredData, sortName, sortDirection],
  );

  const columns = [
    {
      name: 'user',
      label: 'Client Name',
      options: {
        customBodyRender: (value, tableMeta) => {
          const rowData = sortedData[tableMeta.rowIndex];
          const userName = rowData?.user?.name || rowData?.user?.first_name+' '+rowData?.user?.last_name;
          const userImage = rowData?.user?.image_url || DefaultAvatar;
          return (
            <div className="d-flex align-items-center gap-8">
              <img style={{ height: "35px", width: "35px", borderRadius: "50%", objectFit: "cover" }} src={userImage} alt="client" onError={(e) => { e.target.src = DefaultAvatar; }} />
              <span>{userName}</span>
            </div>
          );
        }
      }
    },
    {
      name: 'products',
      label: 'Products',
      options: {
        customBodyRender: (value, tableMeta) => {
          const rowData = sortedData[tableMeta.rowIndex];
          return <span className="font-medium products-desc">{getUniqueProducts(rowData)}</span>;
        }
      }
    },
    {
      name: 'created_at',
      label: 'Date',
      options: {
        customBodyRender: (value) => <span className="text-gray-600">{formatDate(value)}</span>
      }
    },
    {
      name: 'total_amount',
      label: 'Price',
      options: {
        customBodyRender: (value, tableMeta) => {
          const rowData = sortedData[tableMeta.rowIndex];
          const amount = rowData?.total_amount || rowData?.summary?.total_amount || rowData?.summary?.subtotal || 0;
          return <span className="font-bold col-packageType">${parseFloat(amount || 0).toFixed(2)}</span>;
        }
      }
    },
    {
      name: 'status',
      label: 'Order Status',
      options: {
        customBodyRender: (value, tableMeta) => {
          const rowData = sortedData[tableMeta.rowIndex];
          const orderId = rowData?.id || rowData?.order_id;
          const isOpen = openDropdown === orderId;
          const displayStatus = value?.toLowerCase() === 'delivered' ? 'shipped' : value;
          return (
            <div className="position-relative payment-status-dropdown">
              <div 
                className={`col-payment-status ${getStatusColor(value)}`}
                style={{ cursor: updatingOrderStatus ? 'not-allowed' : 'pointer', display: 'inline-block', padding: '4px 12px', borderRadius: '20px', opacity: updatingOrderStatus ? 0.6 : 1 }}
                onClick={(event) => !updatingOrderStatus && handleStatusDropdownToggle(event, orderId, isOpen)}
              >
                {updatingOrderStatus && isOpen ? (
                  <>
                    <div className="spinner-border spinner-border-sm me-1" role="status" style={{ width: '12px', height: '12px' }}></div>
                    Updating...
                  </>
                ) : (
                  <>
                    {displayStatus || '-'}
                    <Icon icon="mdi:chevron-down" width="16" height="16" style={{ marginLeft: '4px', verticalAlign: 'middle' }} />
                  </>
                )}
              </div>
              {isOpen && (
                <div className="position-absolute" style={{ [dropdownDirection === 'up' ? 'bottom' : 'top']: '100%', left: 0, zIndex: 1000, backgroundColor: 'white', border: '1px solid #ddd', borderRadius: '8px', boxShadow: '0 2px 8px rgba(0,0,0,0.15)', minWidth: '120px' }}>
                  {statusOptions.map((status) => (
                    <div
                      key={status.value}
                      className="value"
                      style={{ 
                        cursor: updatingOrderStatus ? 'not-allowed' : 'pointer', 
                        fontSize: '14px', 
                        padding: '8px 12px',
                        opacity: updatingOrderStatus ? 0.6 : 1,
                        pointerEvents: updatingOrderStatus ? 'none' : 'auto'
                      }}
                      onClick={() => !updatingOrderStatus && handleStatusChange(orderId, status.value)}
                    >
                      {status.label}
                    </div>
                  ))}
                </div>
              )}
            </div>
          );
        }
      }
    },
    {
      name: 'payment_method',
      label: 'Method',
      options: {
        customBodyRender: (value) => (
          <div className="d-flex align-items-center gap-2">
            <Icon icon={getPaymentMethodIcon(value)} width="20" height="20" />
            <span>{value || '-'}</span>
          </div>
        )
      }
    },
    {
      name: 'action',
      label: 'Action',
      options: {
        filter: false,
        sort: false,
        customBodyRenderLite: (dataIndex) => {
          const rowData = sortedData[dataIndex]; 
          const orderId = rowData?.id || rowData?.order_id;
          return (
            <div className='d-flex justify-content-center'>
              <Icon
                className="editBtn hover: cursor-pointer"
                icon="boxicons:eye"
                width="24"
                height="24"
                onClick={() =>
                  navigate(`/order/${orderId}`, {
                    state: getReturnState(),
                  })
                }
              />
              <Icon onClick={() => handleDeleteOrder(orderId)} className="deleteBtn hover:cursor-pointer" icon="material-symbols:delete-outline" width="24" height="24" style={{ cursor: 'pointer' }} />
            </div>
          );
        },
      },
    },
  ];

  const tableTotal = isLocalFilteredView ? sortedData.length : ordersTotal;

  useEffect(() => {
    if (page > 0 && tableTotal <= page * rowsPerPage) {
      setTableState({ page: 0 });
    }
  }, [page, rowsPerPage, setTableState, tableTotal]);

  const options = {
    selectableRows: 'none',
    rowsPerPage,
    rowsPerPageOptions: getRowsPerPageOptions(tableTotal),
    responsive: 'standard',
    elevation: 0,
    print: false,
    download: false,
    viewColumns: false,
    filter: false,
    search: true,
    sortOrder: getSortOrder(sortName, sortDirection),
    onColumnSortChange: (changedColumn, direction) => {
      setTableState({
        sortName: direction === 'none' ? '' : changedColumn,
        sortDirection: direction === 'none' ? '' : direction,
      });
    },
    searchText,
    onSearchChange: (searchValue) => {
      setIsSearchUpdate(true);
      setTableState({ searchText: searchValue || '', page: 0 });
    },
    serverSide: !isLocalFilteredView,
    count: tableTotal,
    page,
    onChangePage: (newPage) => {
      setTableState({ page: newPage });
    },
    onChangeRowsPerPage: (numberOfRows) => {
      setTableState({ rowsPerPage: numberOfRows, page: 0 });
    },
  };

  if (loading && !isSearchUpdate) {
    return (
      <div className="card packageTable">
        <div className="card-body text-center py-5">
          <div className="spinner-border text-primary" role="status"><span className="visually-hidden">Loading...</span></div>
          <p className="mt-3">Loading orders...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="card packageTable">
        <div className="card-body text-center py-5">
          <div className="text-danger">
            <Icon icon="material-symbols:error-outline" width="48" height="48" />
            <p className="mt-3">Error: {typeof error === 'string' ? error : 'Failed to load orders'}</p>
            <button
              className="btn btn-primary mt-3"
              onClick={() =>
                dispatch(
                  getOrders({
                    page: requestPage,
                    perPage: requestPerPage,
                    search: hasSearch ? searchText : '',
                  }),
                )
              }
            >
              Retry
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="card packageTable orders-page-table">
      <div className="card-header d-flex justify-content-between align-items-center">
        <div className="tableHeading">
          <h3 className='fs-3 fw-semibold'>{filter === "all" ? "All Orders" : "Monthly Orders"}</h3>
        </div>
        <div className="d-flex gap-3 align-items-center">
          {/* Status Filter Dropdown */}
          <div className="status-filter-dropdown2">
            <select 
              className="form-select" 
              value={statusFilter} 
              onChange={(e) => setStatusFilter(e.target.value)}
            >
              {statusFilterOptions.map(option => (
                <option key={option.value} value={option.value}>
                  {option.label}
                </option>
              ))}
            </select>
          </div>
          
          {/* Date Toggle */}
          <div className="custom-toggle">
            <div className="toggle-container">
              <div className={`toggle-indicator ${filter === "all" ? "right" : "left"}`} />
              <div className={`toggle-option ${filter === "monthly" ? "active" : ""}`} onClick={() => setTableState({ filter: "monthly", page: 0 })}>
                <Icon icon="mdi:calendar-month" width="22" /><span>This Month</span>
              </div>
              <div className={`toggle-option ${filter === "all" ? "active" : ""}`} onClick={() => setTableState({ filter: "all", page: 0 })}>
                <Icon icon="mdi:calendar-today" width="25" /><span>All Time</span>
              </div>
            </div>
          </div>
        </div>
      </div>
      <div className="card-body">
        <MUIDataTable data={sortedData} columns={columns} options={options} className="overflow-hidden ordersTable" />
      </div>
    </div>
  );
};

export default OrdersDataTable;
